import { useState, useEffect, useRef, useCallback } from 'react';
import { Lock, Monitor, Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { apiClient } from '@/api/client';
import { useMobileStore } from '@/stores/mobile-store';
import { useTunnelStore } from '@/stores/tunnel-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useAppStore } from '@/stores/app-store';
import { TERMINAL_THEMES } from '@/config/themes';
import { SkeletonText } from '@/components/shared/Skeleton';
import type { TunnelConfig } from '@/types';

// ═══════════════════════════════════════════
// Extra Keys Toolbar
// ═══════════════════════════════════════════

interface ExtraKey {
  label: string;
  key?: string;
  modifier?: boolean;
}

const EXTRA_KEYS: ExtraKey[] = [
  { label: 'Tab', key: '\t' },
  { label: 'Ctrl', modifier: true },
  { label: 'Esc', key: '\x1b' },
  { label: '\u2191', key: '\x1b[A' },
  { label: '\u2193', key: '\x1b[B' },
  { label: '\u2190', key: '\x1b[D' },
  { label: '\u2192', key: '\x1b[C' },
  { label: '|', key: '|' },
  { label: '/', key: '/' },
  { label: '~', key: '~' },
];

interface ExtraKeysToolbarProps {
  onKey: (key: string) => void;
  onCtrlKey: (key: string) => void;
}

function ExtraKeysToolbar({ onKey, onCtrlKey }: ExtraKeysToolbarProps) {
  const [ctrlActive, setCtrlActive] = useState(false);

  const handlePress = useCallback(
    (ek: ExtraKey) => {
      if (ek.modifier) {
        setCtrlActive((prev) => !prev);
        return;
      }
      if (ctrlActive && ek.key && ek.key.length === 1) {
        // Send Ctrl+key: char code 1-26 for a-z
        const code = ek.key.toLowerCase().charCodeAt(0) - 96;
        if (code >= 1 && code <= 26) {
          onCtrlKey(String.fromCharCode(code));
        } else {
          onKey(ek.key);
        }
        setCtrlActive(false);
      } else if (ek.key) {
        onKey(ek.key);
        setCtrlActive(false);
      }
    },
    [ctrlActive, onKey, onCtrlKey],
  );

  return (
    <div
      className="flex gap-1 px-2 py-1.5 shrink-0 overflow-x-auto border-t"
      style={{
        backgroundColor: 'var(--dbgo-sidebar)',
        borderColor: 'var(--border-default)',
      }}
    >
      {EXTRA_KEYS.map((ek) => (
        <button
          key={ek.label}
          onClick={() => handlePress(ek)}
          className={`flex items-center justify-center rounded-md text-xs font-medium shrink-0 ${
            ek.modifier && ctrlActive
              ? 'bg-accent-blue text-white'
              : 'text-text-primary active:bg-dbgo-tab-hover'
          }`}
          style={{
            height: 44,
            minWidth: 44,
            backgroundColor: ek.modifier && ctrlActive ? undefined : 'var(--dbgo-panel)',
          }}
        >
          {ek.label}
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════
// Terminal List (root screen)
// ═══════════════════════════════════════════

export function TerminalListScreen() {
  const pushScreen = useMobileStore((s) => s.pushScreen);
  const activeTunnels = useTunnelStore((s) => s.activeTunnels);

  const { data: tunnels = [], isLoading } = useQuery<TunnelConfig[]>({
    queryKey: ['tunnels'],
    queryFn: () => apiClient.get<TunnelConfig[]>('/api/tunnels'),
  });

  // Track active terminal sessions per tunnel
  const [activeSessions, setActiveSessions] = useState<
    Array<{ tunnelId: string; tunnelName: string; sessionId: string }>
  >([]);

  const handleOpenTerminal = useCallback(
    (tunnel: TunnelConfig) => {
      const sessionId = `mobile-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      setActiveSessions((prev) => [
        ...prev,
        { tunnelId: tunnel.id, tunnelName: tunnel.name, sessionId },
      ]);
      pushScreen({
        type: 'terminal-view',
        title: `${tunnel.name} - Terminal`,
        params: { tunnelId: tunnel.id, tunnelName: tunnel.name },
      });
    },
    [pushScreen],
  );

  const handleResumeSession = useCallback(
    (session: { tunnelId: string; tunnelName: string; sessionId: string }) => {
      pushScreen({
        type: 'terminal-view',
        title: `${session.tunnelName} - Terminal`,
        params: { tunnelId: session.tunnelId, tunnelName: session.tunnelName, sessionId: session.sessionId },
      });
    },
    [pushScreen],
  );

  return (
    <div className="h-full overflow-y-auto">
      {/* Available Tunnels */}
      <div className="px-4 pt-3">
        <div className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
          Available Tunnels
        </div>
        {isLoading && <SkeletonText lines={3} />}
        {!isLoading && tunnels.length === 0 && (
          <div className="text-sm text-text-muted py-4 text-center">
            No tunnels configured
          </div>
        )}
        <div className="space-y-2">
          {tunnels.map((tunnel) => {
            const tunnelState = activeTunnels[tunnel.id];
            const isRunning = tunnelState?.status === 'running';
            return (
              <div
                key={tunnel.id}
                className="rounded-lg border px-3 py-2.5"
                style={{
                  backgroundColor: 'var(--dbgo-panel)',
                  borderColor: 'var(--border-default)',
                }}
              >
                <div className="flex items-center gap-2">
                  <Lock size={16} className={isRunning ? 'text-accent-green' : 'text-text-muted'} />
                  <span className="text-sm text-text-primary font-medium">{tunnel.name}</span>
                </div>
                <div className="text-xs text-text-muted mt-0.5 ml-6">
                  {tunnel.username}@{tunnel.host}:{tunnel.port}
                </div>
                <div className="mt-2 ml-6">
                  <button
                    onClick={() => handleOpenTerminal(tunnel)}
                    className="flex items-center gap-1.5 rounded-md px-3 text-xs font-medium text-white"
                    style={{
                      height: 32,
                      backgroundColor: 'var(--accent-blue)',
                    }}
                  >
                    <Plus size={14} />
                    Open Terminal
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <div className="px-4 pt-4 pb-4">
          <div className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
            Active Sessions
          </div>
          <div className="space-y-2">
            {activeSessions.map((session) => (
              <button
                key={session.sessionId}
                onClick={() => handleResumeSession(session)}
                className="w-full text-left rounded-lg border px-3 py-2.5 active:bg-dbgo-tab-hover transition-colors"
                style={{
                  backgroundColor: 'var(--dbgo-panel)',
                  borderColor: 'var(--border-default)',
                }}
              >
                <div className="flex items-center gap-2">
                  <Monitor size={16} className="text-accent-green" />
                  <span className="text-sm text-text-primary">
                    {session.tunnelName} &mdash; Terminal
                  </span>
                </div>
                <div className="text-xs text-text-muted mt-0.5 ml-6">
                  Session {session.sessionId.slice(0, 8)}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// Terminal View (pushed, full-screen xterm)
// ═══════════════════════════════════════════

type TerminalState = 'connecting' | 'open' | 'disconnected' | 'closed' | 'error';

export function MobileTerminalViewScreen({ params }: { params: Record<string, unknown> }) {
  const tunnelId = params.tunnelId as string;
  const initialSessionId = params.sessionId as string | undefined;

  const [state, setState] = useState<TerminalState>('connecting');
  const [bgColor, setBgColor] = useState('#0e1015');

  const containerRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const resizeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef<TerminalState>(state);
  stateRef.current = state;
  const sessionIdRef = useRef<string | undefined>(initialSessionId);

  const connect = useCallback(() => {
    if (!containerRef.current) return;

    // Clean up existing
    wsRef.current?.close();
    wsRef.current = null;
    terminalRef.current?.dispose();
    terminalRef.current = null;

    setState('connecting');

    const ss = useSettingsStore.getState();
    const termFontSize = ss.getNumber('terminal.fontSize', 13);
    const termFontFamily = ss.get('terminal.fontFamily', "'JetBrains Mono', monospace");
    const termCursorBlink = ss.getBool('terminal.cursorBlink', true);
    const termScrollback = ss.getNumber('terminal.scrollback', 10000);
    const currentAppTheme = useAppStore.getState().theme;
    const termThemeKey = currentAppTheme === 'dark' ? 'terminal.theme.dark' : 'terminal.theme.light';
    const termThemeDefault = currentAppTheme === 'dark' ? 'dbgo-dark' : 'dbgo-light';
    const termThemeId = ss.get(termThemeKey, termThemeDefault);
    const termTheme = TERMINAL_THEMES.find((t) => t.id === termThemeId) ?? TERMINAL_THEMES[0];
    setBgColor(termTheme.colors.background);

    const terminal = new Terminal({
      theme: termTheme.colors,
      cursorBlink: termCursorBlink,
      fontSize: termFontSize,
      fontFamily: termFontFamily,
      scrollback: termScrollback,
    });

    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    terminal.loadAddon(new WebLinksAddon());
    terminal.open(containerRef.current);
    fitAddon.fit();

    terminalRef.current = terminal;
    fitAddonRef.current = fitAddon;

    // Touch scrolling: mobile sessions don't use tmux (see persistent: false
    // below), so xterm.js's own scrollback buffer holds the history and
    // terminal.scrollLines() works directly. Much simpler than fighting tmux.
    attachTouchScroll(containerRef.current, terminal);

    const token = localStorage.getItem('dbgo_token');
    if (!token) {
      terminal.writeln('\r\n\x1b[31mError: Not authenticated\x1b[0m');
      setState('error');
      return;
    }

    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${location.host}/ws/terminal?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => {
      // Mobile always opens a fresh non-persistent session — no tmux, so no
      // resume/reconnect path. Mobile sessions are short-lived by design;
      // dropping tmux gives us native xterm scrollback and removes a pile of
      // remote-config dependencies that kept breaking touch scrolling.
      ws.send(JSON.stringify({
        type: 'open',
        tunnelId,
        cols: terminal.cols,
        rows: terminal.rows,
        persistent: false,
      }));
    };

    ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        terminal.write(new Uint8Array(event.data));
        return;
      }
      if (typeof event.data !== 'string') return;

      try {
        const msg = JSON.parse(event.data) as {
          type: string;
          sessionId?: string;
          message?: string;
          persistent?: boolean;
          code?: number;
        };

        switch (msg.type) {
          case 'opened':
            sessionIdRef.current = msg.sessionId;
            if (msg.persistent) {
              terminal.writeln('\x1b[2m tmux session active\x1b[0m');
            }
            setState('open');
            break;
          case 'resumed':
            setState('open');
            break;
          case 'reconnected':
            terminal.writeln('\x1b[2m Session reconnected via tmux\x1b[0m');
            setState('open');
            break;
          case 'exit':
            terminal.writeln(`\r\n\x1b[33mSession ended (exit code ${msg.code ?? 0}). Tap Reconnect.\x1b[0m`);
            setState('closed');
            break;
          case 'error':
            if (sessionIdRef.current && msg.message?.includes('Session not found')) {
              terminal.writeln('\x1b[2m Reconnecting via tmux...\x1b[0m');
              ws.send(JSON.stringify({
                type: 'reconnect',
                sessionId: sessionIdRef.current,
                tunnelId,
                cols: terminal.cols,
                rows: terminal.rows,
              }));
              return;
            }
            if (sessionIdRef.current && msg.message?.includes('expired')) {
              terminal.writeln(`\r\n\x1b[31m${msg.message}\x1b[0m`);
              sessionIdRef.current = undefined;
              setState('closed');
              return;
            }
            terminal.writeln(`\r\n\x1b[31mError: ${msg.message}\x1b[0m`);
            setState('error');
            break;
        }
      } catch {
        terminal.write(event.data);
      }
    };

    ws.onclose = () => {
      const s = stateRef.current;
      if (s !== 'closed' && s !== 'error') {
        setState('disconnected');
      }
    };

    ws.onerror = () => {
      if (stateRef.current !== 'closed') {
        setState('error');
      }
    };

    terminal.onData((data) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(data);
      }
    });
  }, [tunnelId]);

  // Mount
  useEffect(() => {
    connect();

    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimer.current) clearTimeout(resizeTimer.current);
      resizeTimer.current = setTimeout(() => {
        const fitAddon = fitAddonRef.current;
        const terminal = terminalRef.current;
        if (!fitAddon || !terminal) return;
        fitAddon.fit();
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'resize',
            cols: terminal.cols,
            rows: terminal.rows,
          }));
        }
      }, 100);
    });
    resizeObserver.observe(container);

    return () => {
      wsRef.current?.close();
      wsRef.current = null;
      terminalRef.current?.dispose();
      terminalRef.current = null;
      fitAddonRef.current = null;
      resizeObserver.disconnect();
      if (resizeTimer.current) clearTimeout(resizeTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reconnect = useCallback(() => {
    sessionIdRef.current = undefined;
    connect();
  }, [connect]);

  const handleExtraKey = useCallback((key: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(key);
    }
    terminalRef.current?.focus();
  }, []);

  const handleCtrlKey = useCallback((key: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(key);
    }
    terminalRef.current?.focus();
  }, []);

  return (
    <div className="h-full flex flex-col relative">
      {/* Terminal container */}
      <div className="flex-1 min-h-0 p-1" style={{ backgroundColor: bgColor }}>
        <div
          ref={containerRef}
          className="h-full w-full overflow-hidden"
          style={{ touchAction: 'none' }}
        />
      </div>

      {/* Extra keys toolbar */}
      <ExtraKeysToolbar onKey={handleExtraKey} onCtrlKey={handleCtrlKey} />

      {/* Overlay states */}
      {state === 'connecting' && (
        <div className="absolute inset-0 flex items-center justify-center bg-dbgo-root/80 z-10">
          <span className="text-text-muted text-sm">Connecting...</span>
        </div>
      )}
      {state === 'disconnected' && (
        <div className="absolute inset-0 flex items-center justify-center bg-dbgo-root/80 z-10">
          <div className="text-center">
            <span className="text-accent-yellow text-sm block mb-2">Disconnected</span>
            <button onClick={reconnect} className="text-sm text-accent-blue active:underline px-4 py-2">
              Reconnect
            </button>
          </div>
        </div>
      )}
      {(state === 'error' || state === 'closed') && (
        <div className="absolute inset-0 flex items-center justify-center bg-dbgo-root/80 z-10">
          <div className="text-center">
            <span className={`text-sm block mb-2 ${state === 'error' ? 'text-accent-red' : 'text-accent-yellow'}`}>
              {state === 'error' ? 'Connection failed' : 'Session ended'}
            </span>
            <button onClick={reconnect} className="text-sm text-accent-blue active:underline px-4 py-2">
              {state === 'error' ? 'Retry' : 'New Session'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// Touch scroll helper
// ═══════════════════════════════════════════

/**
 * Translate vertical touch swipes into xterm scrollback. Mobile uses a plain
 * (non-tmux) shell, so xterm.js owns the scrollback buffer and
 * terminal.scrollLines() scrolls it directly — no WS round-trip, no remote
 * dependencies. Calls preventDefault() on touchmove so the page doesn't
 * scroll underneath.
 *
 * Note: full-screen alt-screen apps (vim, less, top) still have no scrollback
 * to scroll — that's expected, just like in a real terminal.
 */
function attachTouchScroll(container: HTMLElement, terminal: Terminal): void {
  let lastY: number | null = null;
  let accumulated = 0;

  const rowHeight = (): number => {
    const h = container.clientHeight;
    return terminal.rows > 0 ? h / terminal.rows : 17;
  };

  const onStart = (e: TouchEvent) => {
    if (e.touches.length !== 1) {
      lastY = null;
      return;
    }
    lastY = e.touches[0].clientY;
    accumulated = 0;
  };

  const onMove = (e: TouchEvent) => {
    if (lastY === null || e.touches.length !== 1) return;
    const y = e.touches[0].clientY;
    const dy = y - lastY;
    lastY = y;
    accumulated += dy;

    const rh = rowHeight();
    const lines = Math.trunc(accumulated / rh);
    if (lines !== 0) {
      // Drag-down (positive dy) → scroll up (negative scrollLines) to show
      // older content — natural "drag the content" feel.
      terminal.scrollLines(-lines);
      accumulated -= lines * rh;
    }

    if (e.cancelable) e.preventDefault();
  };

  const onEnd = () => {
    lastY = null;
    accumulated = 0;
  };

  // passive: false is required for preventDefault() to take effect.
  // capture: true ensures we run before any handlers xterm may have attached.
  container.addEventListener('touchstart', onStart, { passive: true, capture: true });
  container.addEventListener('touchmove', onMove, { passive: false, capture: true });
  container.addEventListener('touchend', onEnd, { passive: true, capture: true });
  container.addEventListener('touchcancel', onEnd, { passive: true, capture: true });
}
